import { onScopeDispose } from "vue";

/**
 * QUANTO DO FUNDO DA JANELA O TECLADO ESTÁ A COMER, em pixels, na variável
 * `--kb-inset` do documento.
 *
 * ── POR QUE ISTO EXISTE ───────────────────────────────────────────────────
 *
 * Uma superfície ancorada no fundo (o compositor do Ben, o da caixa de entrada)
 * mede a janela; o teclado do telefone não é janela. No Android isto já está
 * resolvido pela chave `interactive-widget=resizes-content` do `index.html` — a
 * janela encolhe e o `dvh` encolhe com ela. O Safari do iPhone ainda não lê essa
 * chave: lá a janela de LAYOUT continua do mesmo tamanho e o teclado sobe por
 * cima, deixando o campo de escrever debaixo dele — visível para o CSS, fora de
 * alcance para o dedo.
 *
 * A `visualViewport` é a única a saber a diferença: ela é a parte que a pessoa
 * de facto vê. O que sobra entre ela e a janela de layout é o teclado.
 *
 * ── PORQUE UMA VARIÁVEL E NÃO UMA ALTURA ──────────────────────────────────
 *
 * Quem tem de encolher é a superfície, e cada uma encolhe à sua maneira (a
 * gaveta com recuo no fundo, uma folha de baixo com menos altura). A variável
 * diz o FACTO uma vez; a decisão fica com quem desenha. E onde a chave do
 * viewport já funciona o número dá zero por si — nada compensa duas vezes.
 *
 * Chamada UMA vez, na casca.
 */
export function useKeyboardInset(): void {
	const viewport = typeof window === "undefined" ? null : window.visualViewport;
	if (!viewport) return;

	function apply(): void {
		if (!viewport) return;
		// SÓ HÁ TECLADO COM UM CAMPO FOCADO (19/09). O Safari do iPhone também
		// desalinha `innerHeight` e a janela visível quando a barra de ferramentas
		// aparece e some ao rolar — e essa diferença, lida como teclado, encolhia
		// a casca inteira: a lista de registros acabava a um terço do fim da tela,
		// com um vazio embaixo. Um campo focado é o único sinal de teclado que existe.
		if (!typing()) {
			document.documentElement.style.setProperty("--kb-inset", "0px");
			return;
		}
		// `offsetTop` entra na conta: o iOS também DESLIZA a janela visível para
		// mostrar o campo focado, e sem isso o recuo sairia grande demais.
		const eaten = window.innerHeight - viewport.height - viewport.offsetTop;
		// Abaixo de um dedo não é teclado, é arredondamento da barra do navegador.
		const inset = eaten > 40 ? Math.round(eaten) : 0;
		document.documentElement.style.setProperty("--kb-inset", `${inset}px`);
	}

	apply();
	viewport.addEventListener("resize", apply);
	viewport.addEventListener("scroll", apply);
	// O foco muda antes de a janela visível mexer: sem estes dois, fechar o
	// teclado tocando fora do campo deixava o recuo do último `resize` no ar.
	document.addEventListener("focusin", apply);
	document.addEventListener("focusout", apply);

	onScopeDispose(() => {
		viewport.removeEventListener("resize", apply);
		viewport.removeEventListener("scroll", apply);
		document.removeEventListener("focusin", apply);
		document.removeEventListener("focusout", apply);
		document.documentElement.style.removeProperty("--kb-inset");
	});
}

/** Whether something that summons a keyboard has the focus right now. */
function typing(): boolean {
	const el = document.activeElement;
	if (!el || el === document.body) return false;
	const tag = el.tagName;
	if (tag === "TEXTAREA" || tag === "SELECT") return true;
	if (tag === "INPUT") {
		const type = (el as HTMLInputElement).type;
		return !["button", "checkbox", "radio", "range", "submit", "reset", "file", "color"].includes(
			type,
		);
	}
	return (el as HTMLElement).isContentEditable === true;
}
